import classNames from 'classnames'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { CloseAllTradesModal } from '@pages/Trade/Portfolio/Modals/CloseAllTradesModal'
import { CloseTradeModal } from '@pages/Trade/Portfolio/Modals/CloseTradeModal'
import { TPSLModal } from '@pages/Trade/Portfolio/Modals/TPSLModal'
import { TradeDetailsOpenModal } from '@pages/Trade/Portfolio/Modals/TradeDetailsOpenModal'
import { getStepDecimals } from '@pages/Trade/utils/helpers'

import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Truncate } from '@ui/Truncate/Truncate'
import { Body2 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'
import { IconButton } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip'
import { CloseIcon } from '@ui/icons/Close.icon'
import {
  ExpandableRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableRow,
} from '@ui/v2/Table/Table'

import { SortOrder } from '@utils/ApiClient'

import type { DxTradeExecutableSide } from '@model/DxTrade.types'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { useFixTradingWebSocketContext } from '../context/FixTradingWebSocketContext'
import { useCurrentPricesForTrades } from '../hooks/useCurrentPricesStore'
import type { DxtradeInstrumentMap } from '../hooks/useDxtradeInstruments'
import type { PortfolioTrade } from '../hooks/useDxtradePortfolioTrades'
import {
  buildTradeWidgetUrl,
  formatCurrency,
  formatDate,
  formatSymbol,
  renderCellValue,
  renderSide,
} from '../utils/formatters'
import { calculatePnL } from '../utils/functions'
import EditableCellContent from './EditableCellContent'
import { EmptyPortfolioState } from './EmptyPortfolioState'
import { LivePriceCell } from './LivePriceCell'
import RowActions from './RowActions'
import { GroupPnL, TradePnL } from './TradePnL'

import styles from './PortfolioTradesTable.module.scss'

type Props = {
  trades: PortfolioTrade[]
  isLoading: boolean
  accountCurrency?: CurrencyType
  accountCode?: string
  dxtradeInstruments: DxtradeInstrumentMap
  hasActiveFilters?: boolean
}

function PortfolioTradesTableComponent({
  trades,
  isLoading,
  accountCurrency,
  accountCode,
  dxtradeInstruments,
  hasActiveFilters,
}: Props) {
  const { t } = useTranslation()
  const { formatNumber } = useFormatNumber()
  const navigate = useNavigate()

  const handleRowNavigate = useCallback(
    (symbol: string, side: DxTradeExecutableSide) => {
      navigate(buildTradeWidgetUrl(symbol, side))
    },
    [navigate]
  )

  const { newOrder, replaceOrder, closePosition, closeAllPositions, cancelOrder } =
    useFixTradingWebSocketContext()
  const tpslModal = useModal(TPSLModal)
  const tradeDetailsOpenModal = useModal(TradeDetailsOpenModal)
  const closeTradeModal = useModal(CloseTradeModal, { closePosition })
  const closeAllTradesModal = useModal(CloseAllTradesModal, { closeAllPositions })

  const currentPrices = useCurrentPricesForTrades(trades ?? [], 'position')

  const pnlByTradeId = useMemo(() => {
    const map = new Map<string, number>()
    ;(trades ?? []).forEach((trade, i) => {
      const pnl = calculatePnL({
        side: trade.side,
        openPrice: trade.entryPrice,
        quantity: trade.quantity,
        exitPrice: currentPrices[i] ?? null,
      })
      if (pnl != null) map.set(trade.tradeId, pnl)
    })
    return map
  }, [trades, currentPrices])

  const noResults = !trades?.length ? (
    <EmptyPortfolioState hasActiveFilters={hasActiveFilters} />
  ) : (
    false
  )

  const groups = useMemo(() => {
    const map = new Map<string, PortfolioTrade[]>()
    for (const trade of trades || []) {
      const key = trade.symbol
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(trade)
    }
    return Array.from(map, ([symbol, list]) => ({ symbol, trades: list }))
  }, [trades])

  const [sortField, setSortField] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<SortOrder>('ASC')

  const handleSetSort = useCallback((id: string, order: SortOrder) => {
    setSortField(id)
    setSortOrder(order)
  }, [])

  const sortedGroups = useMemo(() => {
    if (!sortField) return groups

    const sumNum = (arr: PortfolioTrade[], key: keyof PortfolioTrade) =>
      arr.reduce<number>((acc, t) => acc + (typeof t[key] === 'number' ? (t[key] as number) : 0), 0)

    const groupPnL = (arr: PortfolioTrade[]) =>
      arr.reduce<number>((acc, t) => acc + (pnlByTradeId.get(t.tradeId) ?? 0), 0)

    const getValue = (group: { symbol: string; trades: PortfolioTrade[] }): string | number => {
      const first = group.trades[0]
      switch (sortField) {
        case 'type':
          return first.side ?? ''
        case 'openedOn':
          return new Date(first.openedOn).getTime()
        case 'size':
          return sumNum(group.trades, 'sizeLots')
        case 'entryPrice':
          return first.entryPrice ?? 0
        case 'margin':
          return sumNum(group.trades, 'margin')
        case 'pnl':
          return groupPnL(group.trades)
        default:
          return group.symbol
      }
    }

    const sorted = [...groups].sort((a, b) => {
      const va = getValue(a)
      const vb = getValue(b)
      if (va < vb) return sortOrder === 'ASC' ? -1 : 1
      if (va > vb) return sortOrder === 'ASC' ? 1 : -1
      return 0
    })

    return sorted
  }, [groups, sortField, sortOrder, pnlByTradeId])

  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({})

  const toggleExpanded = (symbol: string) =>
    setExpandedMap((prev) => ({ ...prev, [symbol]: !prev[symbol] }))

  const ensureExpansionKeys = useMemo(() => groups.map((group) => group.symbol).join('|'), [groups])
  useEffect(() => {
    setExpandedMap((prev) => {
      const next: Record<string, boolean> = {}
      for (const group of groups) next[group.symbol] = prev[group.symbol] ?? false
      return next
    })
  }, [ensureExpansionKeys, groups])

  return (
    <>
      <div className={styles.localExpandedOverrides}>
        <Table
          isLoading={isLoading}
          sticky='left'
          noResults={noResults}
          skeletonRows={5}
          skeletonColumns={11}
        >
          <TableHeader>
            <TableRow>
              <TableHead>{t('Portfolio.Asset')}</TableHead>
              <TableHeadSort
                id='type'
                sort={sortField}
                sortOrder={sortOrder}
                setSort={handleSetSort}
              >
                {t('Portfolio.Type')}
              </TableHeadSort>
              <TableHeadSort
                id='openedOn'
                sort={sortField}
                sortOrder={sortOrder}
                setSort={handleSetSort}
                align='right'
              >
                {t('Portfolio.Opened Date')}
              </TableHeadSort>
              <TableHeadSort
                id='size'
                sort={sortField}
                sortOrder={sortOrder}
                setSort={handleSetSort}
                align='right'
              >
                {t('Portfolio.Size')}
              </TableHeadSort>
              <TableHeadSort
                id='entryPrice'
                sort={sortField}
                sortOrder={sortOrder}
                setSort={handleSetSort}
                align='right'
              >
                {t('Portfolio.Entry Price')}
              </TableHeadSort>
              <TableHeadSort align='right'>{t('Portfolio.Current price')}</TableHeadSort>
              <TableHeadSort
                id='margin'
                sort={sortField}
                sortOrder={sortOrder}
                setSort={handleSetSort}
                align='right'
              >
                {t('Portfolio.Margin')}
              </TableHeadSort>
              <TableHeadSort align='right'>{t('Portfolio.Stop Loss')}</TableHeadSort>
              <TableHeadSort align='right'>{t('Portfolio.Take Profit')}</TableHeadSort>
              <TableHeadSort
                id='pnl'
                sort={sortField}
                sortOrder={sortOrder}
                setSort={handleSetSort}
                align='right'
              >
                {t('Portfolio.P&L')}
              </TableHeadSort>
              <TableHead align='right'>{t('Portfolio.Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedGroups.map((group) => {
              const count = group.trades.length
              if (count === 1) {
                const row = group.trades[0]
                const fractionDigits = getStepDecimals(row.priceIncrement)

                return (
                  <TableRow
                    key={row.tradeId}
                    onClick={() =>
                      handleRowNavigate(row.symbol, (row.side ?? 'BUY') as DxTradeExecutableSide)
                    }
                  >
                    <TableCell>{formatSymbol(row.symbol, row.description)}</TableCell>
                    <TableCell>{renderSide(row.side, Body2)}</TableCell>
                    <TableCell align='right'>{formatDate(row.openedOn)}</TableCell>
                    <TableCell align='right' sensitive>
                      {renderCellValue(row.sizeLots)}
                    </TableCell>
                    <TableCell align='right' sensitive>
                      {formatNumber(row.entryPrice, {
                        minDecimalPlaces: fractionDigits,
                        maxDecimalPlaces: fractionDigits,
                      }) ?? '-'}
                    </TableCell>
                    <TableCell align='right'>
                      <LivePriceCell trade={row} />
                    </TableCell>
                    <TableCell align='right' sensitive>
                      <span className={styles.marginCell}>
                        {formatCurrency(row.margin, accountCurrency)}
                      </span>
                    </TableCell>
                    <TableCell align='right'>
                      <EditableCellContent
                        value={row.stopLoss}
                        renderValue={(v) =>
                          formatNumber(v, {
                            minDecimalPlaces: fractionDigits,
                            maxDecimalPlaces: fractionDigits,
                          })
                        }
                        editAriaLabel={'Portfolio.Edit Stop Loss'}
                        sensitive
                        onEdit={() => {
                          const instrument = dxtradeInstruments[row.symbol]
                          if (!instrument || instrument.priceIncrement == null) {
                            return
                          }
                          tpslModal.open({
                            position: row,
                            accountCode,
                            accountCurrency,
                            instrumentCurrency: (instrument.currency ?? undefined) as
                              | CurrencyType
                              | undefined,
                            instrumentMeta: {
                              pipSize: instrument.pipSize,
                              priceIncrement: instrument.priceIncrement,
                            },
                            initialActiveType: 'stopLoss',
                            cancelOrder,
                            newOrder,
                            replaceOrder,
                          })
                        }}
                      />
                    </TableCell>
                    <TableCell align='right'>
                      <EditableCellContent
                        value={row.takeProfit}
                        renderValue={(v) =>
                          formatNumber(v, {
                            minDecimalPlaces: fractionDigits,
                            maxDecimalPlaces: fractionDigits,
                          })
                        }
                        editAriaLabel={'Portfolio.Edit Take Profit'}
                        sensitive
                        onEdit={() => {
                          const instrument = dxtradeInstruments[row.symbol]
                          if (!instrument || instrument.priceIncrement == null) {
                            return
                          }
                          tpslModal.open({
                            position: row,
                            accountCode,
                            accountCurrency,
                            instrumentCurrency: (instrument.currency ?? undefined) as
                              | CurrencyType
                              | undefined,
                            instrumentMeta: {
                              pipSize: instrument.pipSize,
                              priceIncrement: instrument.priceIncrement,
                            },
                            initialActiveType: 'takeProfit',
                            cancelOrder,
                            newOrder,
                            replaceOrder,
                          })
                        }}
                      />
                    </TableCell>
                    <TableCell align='right' hasTruncate={false}>
                      <TradePnL trade={row} accountCurrency={accountCurrency} Typography={Body2} />
                    </TableCell>
                    <TableCell align='right'>
                      <RowActions
                        onInfoClick={() => {
                          const instrument = dxtradeInstruments[row.symbol]
                          if (!instrument || instrument.priceIncrement == null) {
                            return
                          }
                          tradeDetailsOpenModal.open({
                            trade: row,
                            accountCurrency,
                            newOrder,
                            closePosition,
                            cancelOrder,
                            replaceOrder,
                            accountCode,
                            instrumentMeta: {
                              pipSize: instrument.pipSize,
                              priceIncrement: instrument.priceIncrement,
                            },
                          })
                        }}
                        onCloseClick={() => {
                          closeTradeModal.open({ trade: row, accountCurrency, accountCode })
                        }}
                        infoAriaLabel={'Portfolio.Info'}
                        closeAriaLabel={'Portfolio.Close'}
                        closeTooltip={t('Portfolio.Close trade tooltip')}
                      />
                    </TableCell>
                  </TableRow>
                )
              }
              const sum = (array: Array<number | undefined | null>) =>
                array.reduce<number>(
                  (acc, value) => (typeof value === 'number' ? acc + value : acc),
                  0
                )

              const totalSize = sum(group.trades.map((trade) => trade.sizeLots)) || undefined

              const id = group.symbol
              const isExpanded = !!expandedMap[id]

              return (
                <ExpandableRow
                  key={id}
                  id={id}
                  row={{
                    id,
                    symbol: group.symbol,
                    subRows: group.trades.map((t) => ({ ...t, id: t.tradeId })),
                  }}
                  isExpanded={isExpanded}
                  onToggle={() => toggleExpanded(id)}
                  className={classNames({
                    [styles.parentCellExpanded]: isExpanded,
                  })}
                  chevronPosition='left'
                  renderParent={() => [
                    {
                      value: (
                        <span className={styles.symbolWithCount}>
                          {formatSymbol(group.symbol, group.trades[0].description)}
                          <Badge type='purple' size='xs' contrast='low'>
                            {count}
                          </Badge>
                        </span>
                      ),
                      align: 'left',
                    },
                    { value: '---', align: 'left' },
                    { value: '---', align: 'right' },
                    {
                      value: <SensitiveData>{renderCellValue(totalSize, 2)}</SensitiveData>,
                      align: 'right',
                    },
                    { value: '---', align: 'right' },
                    { value: <LivePriceCell trade={group.trades[0]} />, align: 'right' },
                    { value: '---', align: 'right' },
                    { value: '---', align: 'right' },
                    { value: '---', align: 'right' },
                    {
                      value: (
                        <GroupPnL
                          trades={group.trades}
                          accountCurrency={accountCurrency}
                          Typography={Body2}
                        />
                      ),
                      align: 'right',
                    },
                    {
                      value: (
                        <Tooltip
                          content={t('Portfolio.Close {{symbol}} trades', { symbol: group.symbol })}
                        >
                          <IconButton.Secondary
                            aria-label={'Close'}
                            onClick={(e) => {
                              e.stopPropagation()
                              if (!accountCode) {
                                return
                              }

                              closeAllTradesModal.open({
                                trades: group.trades,
                                accountCurrency,
                                accountCode,
                                assetSymbol: group.symbol,
                              })
                            }}
                            icon={CloseIcon}
                            size='sm'
                          />
                        </Tooltip>
                      ),
                      align: 'right',
                    },
                  ]}
                  onSubRowClick={(row) =>
                    handleRowNavigate(row.symbol, (row.side ?? 'BUY') as DxTradeExecutableSide)
                  }
                  renderSubRow={(row) => {
                    const fractionDigits = getStepDecimals(row.priceIncrement)
                    return [
                      { value: formatSymbol(row.symbol, row.description), align: 'left' },
                      { value: renderSide(row.side, Body2), align: 'left' },
                      { value: formatDate(row.openedOn), align: 'right' },
                      {
                        value: <SensitiveData>{renderCellValue(row.sizeLots)}</SensitiveData>,
                        align: 'right',
                      },
                      {
                        value: (
                          <SensitiveData>
                            {formatNumber(row.entryPrice, {
                              minDecimalPlaces: fractionDigits,
                              maxDecimalPlaces: fractionDigits,
                            }) ?? '-'}
                          </SensitiveData>
                        ),
                        align: 'right',
                      },
                      { value: <LivePriceCell trade={row} />, align: 'right' },
                      {
                        value: (
                          <SensitiveData>
                            <Truncate>
                              <Body2>
                                <span className={styles.marginCell}>
                                  {formatCurrency(row.margin, accountCurrency)}
                                </span>
                              </Body2>
                            </Truncate>
                          </SensitiveData>
                        ),
                        align: 'right',
                      },
                      {
                        value: (
                          <EditableCellContent
                            value={row.stopLoss}
                            renderValue={(v) =>
                              formatNumber(v, {
                                minDecimalPlaces: fractionDigits,
                                maxDecimalPlaces: fractionDigits,
                              })
                            }
                            editAriaLabel={'Portfolio.Edit Stop Loss'}
                            onEdit={() => {
                              const instrument = dxtradeInstruments[row.symbol]
                              if (!instrument || instrument.priceIncrement == null) {
                                return
                              }

                              tpslModal.open({
                                position: row,
                                accountCode,
                                accountCurrency,
                                instrumentMeta: {
                                  pipSize: instrument.pipSize,
                                  priceIncrement: instrument.priceIncrement,
                                },
                                initialActiveType: 'stopLoss',
                                cancelOrder,
                                newOrder,
                                replaceOrder,
                              })
                            }}
                          />
                        ),
                        align: 'right',
                      },
                      {
                        value: (
                          <EditableCellContent
                            value={row.takeProfit}
                            renderValue={(v) =>
                              formatNumber(v, {
                                minDecimalPlaces: fractionDigits,
                                maxDecimalPlaces: fractionDigits,
                              })
                            }
                            editAriaLabel={'Portfolio.Edit Take Profit'}
                            onEdit={() => {
                              const instrument = dxtradeInstruments[row.symbol]
                              if (!instrument || instrument.priceIncrement == null) {
                                return
                              }
                              tpslModal.open({
                                position: row,
                                accountCode,
                                accountCurrency,
                                instrumentMeta: {
                                  pipSize: instrument.pipSize,
                                  priceIncrement: instrument.priceIncrement,
                                },
                                initialActiveType: 'takeProfit',
                                cancelOrder,
                                newOrder,
                                replaceOrder,
                              })
                            }}
                          />
                        ),
                        align: 'right',
                      },
                      {
                        value: (
                          <TradePnL
                            trade={row}
                            accountCurrency={accountCurrency}
                            Typography={Body2}
                          />
                        ),
                        align: 'right',
                      },
                      {
                        value: (
                          <RowActions
                            onInfoClick={() => {
                              const instrument = dxtradeInstruments[row.symbol]
                              if (!instrument || instrument.priceIncrement == null) {
                                return
                              }
                              tradeDetailsOpenModal.open({
                                trade: row,
                                accountCurrency,
                                newOrder,
                                closePosition,
                                cancelOrder,
                                replaceOrder,
                                accountCode,
                                instrumentMeta: {
                                  pipSize: instrument.pipSize,
                                  priceIncrement: instrument.priceIncrement,
                                },
                              })
                            }}
                            onCloseClick={() => {
                              closeTradeModal.open({ trade: row, accountCurrency, accountCode })
                            }}
                            infoAriaLabel={'Portfolio.Info'}
                            closeAriaLabel={'Portfolio.Close'}
                            closeTooltip={t('Portfolio.Close trade tooltip')}
                          />
                        ),
                        align: 'right',
                      },
                    ]
                  }}
                />
              )
            })}
          </TableBody>
        </Table>
      </div>
    </>
  )
}

export const PortfolioTradesTable = memo(PortfolioTradesTableComponent)
